import { useState } from 'react';
import {
  ActivityTemplate,
  Intro,
  TemplateFields,
  TemplateStatus,
  TemplateTag,
} from '../models';
import { ModuleIdFieldContainer } from './fields/ModuleIdFieldContainer';
import { TemplateImgField } from './fields/TemplateImgField';
import { TemplateStateSwitch } from './fields/TemplateStateSwitch';
import { useNavigate } from 'react-router-dom';
import useLoading from '../contexts/LoadingContext';
import { FieldArray, Form, Formik, FormikErrors } from 'formik';
import TemplateDurationField from './fields/TemplateDurationField';
import TemplateTagField from './fields/TemplateTagField';
import CustomField from './fields/CustomField';
import CustomTextareaField from './fields/CustomTextareaField';
import IntroList from './intro/IntroList';
import { useTranslation } from 'react-i18next';

const genTemplateRequestBody = (values: TemplateFields, status: string) => {
  const formData = new FormData();

  const {
    name,
    description,
    duration,
    file,
    closeModuleId,
    id,
    imageUrl,
    moduleIds,
    materialUrl,
  } = values;
  if (id) {
    formData.set('id', id);
  }
  if (imageUrl) {
    formData.set('imageUrl', imageUrl);
  }
  formData.set('name', name);
  formData.set('description', description);
  formData.set('duration', `${duration}`);
  formData.set('status', status);
  formData.set('moduleIds', JSON.stringify(moduleIds));
  formData.set('closeModuleId', closeModuleId);
  if (materialUrl.length > 0) {
    formData.set('materialUrl', materialUrl);
  }

  if (file) {
    formData.set('coverImage', file);
  }
  return formData;
};

function appendTemplateIntros(
  intros: Intro[],
  isTemplateIntrosUpdated: boolean,
  formData: FormData
) {
  const tempIntros: Intro[] = [];
  intros.forEach((intro) => {
    const { image, ...rest } = intro;

    if (image) {
      formData.append('introImages', image);
    }
    tempIntros.push({ ...rest });
  });

  formData.set('templateIntros', JSON.stringify(tempIntros));

  if (isTemplateIntrosUpdated) {
    formData.set('isTemplateIntrosUpdated', `${isTemplateIntrosUpdated}`);
  }
}

function appendTemplateTag(
  tagOptions: TemplateTag[],
  tagName: string = '',
  formData: FormData
) {
  const tagOptionId = tagOptions.find(
    (tagOption) => tagOption.name === tagName
  )?.id;

  if (typeof tagOptionId === 'number') {
    formData.set('tagId', `${tagOptionId}`);
  }

  if (tagName.length > 0) {
    formData.set('tagName', tagName);
  }
}

export default function TemplateDetail(props: {
  template: ActivityTemplate;
  moduleIds: string[];
  closeModuleId: string;
  intros: Intro[];
  tagOptions: TemplateTag[];
  send: (data: FormData) => Promise<any>;
  refreshTags: () => Promise<TemplateTag[]>;
}) {
  const [t] = useTranslation(['admin', 'common']);
  const { setIsLoading } = useLoading();
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>(TemplateStatus.DRAFT);
  const [invalid, setInvalid] = useState(false);
  const [intros, setIntros] = useState<Intro[]>([...props.intros]);
  const [isTemplateIntrosUpdated, setIsTemplateIntrosUpdated] = useState(false);

  const initialValues: TemplateFields = {
    id: props.template.id,
    imageUrl: props.template.imageUrl,
    name: props.template.name || '',
    duration: props.template.duration || 0,
    description: props.template.description || '',
    file: null,
    closeModuleId: props.closeModuleId || '',
    moduleIds: props.moduleIds.length > 0 ? [...props.moduleIds] : [],
    materialUrl: props.template.materialUrl || '',
    tag: props.template.tag,
    tagName:
      props.tagOptions.find((tag) => tag.id === props.template.tag)?.name || '',
  };

  const validate = (value: TemplateFields) => {
    const errors: FormikErrors<TemplateFields> = {};

    if (
      !value.moduleIds ||
      value.moduleIds.length === 0 ||
      value.moduleIds.some((v: string) => v === '')
    ) {
      errors.moduleIds = 'This value is required.';
    }
    if (!value.closeModuleId) {
      errors.moduleIds = 'This value is required.';
    }
    if (!value.imageUrl && !value.file) {
      errors.file = 'This value is required.';
    }

    setInvalid(Object.keys(errors).length > 0);

    return errors;
  };

  const onSwitchToggle = (isOpen: boolean) => {
    let status = props.template.status || TemplateStatus.DRAFT;

    if (isOpen) {
      status = TemplateStatus.PUBLISHED;
    }

    setStatus(status);
  };

  const onSubmit = (values: TemplateFields) => {
    const formData = genTemplateRequestBody(values, status);
    appendTemplateIntros(intros, isTemplateIntrosUpdated, formData);
    appendTemplateTag(props.tagOptions, values.tagName, formData);

    setIsLoading(true);
    props
      .send(formData)
      .then(() => {
        if (
          formData.get('tagId') === null &&
          formData.get('tagName') !== null
        ) {
          props.refreshTags();
        }
        navigate('/admin');
      })
      .finally(() => setIsLoading(false));
  };

  const onCancel = () => {
    navigate('/admin');
  };

  const onAddIntro = (intro: Intro) => {
    setIntros([...intros, intro]);
    setIsTemplateIntrosUpdated(true);
  };

  const onDeleteIntro = (i: number) => {
    setIntros(intros.filter((_, index) => index !== i));
    setIsTemplateIntrosUpdated(true);
  };

  const onUpdateIntro = (i: number, intro: Intro) => {
    setIntros(intros.map((v, index) => (index === i ? intro : v)));
    setIsTemplateIntrosUpdated(true);
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={validate}
    >
      <Form>
        <div className="h-full w-full flex flex-col">
          <span className="text-4xl text-group-primary font-light mt-12 mb-6">
            {t('edit_template', '活動範本設定')}
          </span>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <CustomField
              title={t('activity_name', '活動名稱')}
              placeholder={t('activity_name_placeholder', '請輸入活動名稱')}
              name="name"
            />
            <TemplateDurationField name="duration" />
          </div>

          <div className="grid grid-cols-4 gap-4 mb-4">
            <div className="col-span-2">
              <CustomTextareaField
                title={t('activity_description', '活動描述')}
                placeholder={t(
                  'activity_description_placeholder',
                  '請輸入活動描述'
                )}
                name="description"
              />
            </div>
            <div className="col-span-2">
              <TemplateTagField
                title={t('activity_tag', '類型')}
                placeholder={t(
                  'activity_tag_placeholder',
                  '選擇或是輸入活動類型'
                )}
                name="tagName"
                tagOptions={props.tagOptions}
                templateTagId={props.template.tag}
              />
            </div>
          </div>

          <TemplateImgField
            title={t('activity_background_image_title', '上傳活動背景圖')}
            name="file"
            imageUrl={props.template.imageUrl}
          />

          <FieldArray name="moduleIds" component={ModuleIdFieldContainer} />

          <div className="grid grid-cols-2 gap-4 mb-4">
            <CustomField
              title={t('activity_close_module_id', '活動關閉對話模組 ID')}
              placeholder={t(
                'activity_close_module_id_placeholder',
                '請填入活動關閉的對話模組 ID'
              )}
              name="closeModuleId"
            />
            <TemplateStateSwitch onChange={onSwitchToggle} />
          </div>

          <IntroList
            intros={intros}
            onAdd={onAddIntro}
            onDelete={onDeleteIntro}
            onUpdate={onUpdateIntro}
          />

          <CustomField
            title={t('activity_material_url', '素材網址')}
            placeholder={t(
              'activity_material_url_placeholder',
              '請輸入素材網址'
            )}
            name="materialUrl"
          />

          <div className="flex justify-center gap-10 my-12">
            <button
              type="button"
              className="border border-group-primary text-group-primary py-3 px-8 rounded text-center disabled:opacity-30 disabled:cursor-not-allowed"
              onClick={onCancel}
            >
              {t('cancel', '取消', { ns: 'common' })}
            </button>
            <button
              type="submit"
              className="bg-group-primary text-white py-3 px-8 rounded text-center disabled:opacity-30 disabled:cursor-not-allowed"
              disabled={invalid}
            >
              {t('submit', '送出', { ns: 'common' })}
            </button>
          </div>
        </div>
      </Form>
    </Formik>
  );
}
